import React from 'react';

import { css } from '@emotion/react';
import { Button, Grid, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import axios from 'axios';
import logger from 'logger';
import { getNextConfigValues } from 'next-config';
import UserContext from 'src/context/user';
import useNonNullContext from 'src/hooks/useNonNullContext';
import { GENERIC_ERROR_MESSAGE, RegexPatterns } from 'src/utils/constants';

const {
  publicRuntimeConfig: { apiUrls },
} = getNextConfigValues();

interface Props {
  isSmallScreen: boolean;
}

const smallScreenStyles = css({
  alignItems: 'center',
  textAlign: 'center',
});

interface FormStatus {
  status: 'unsubmitted' | 'error' | 'success';
  helperText?: string;
}

/**
 * Email subscription form for unauthenticated footer.
 */
const EmailSignup: React.FC<Props> = ({ isSmallScreen }) => {
  const [email, setEmail] = React.useState('');
  const [formStatus, setFormStatus] = React.useState<FormStatus>({
    status: 'unsubmitted',
  });
  const user = useNonNullContext(UserContext);

  const handleSubmit = async () => {
    // shortcut and set validation error state if email pattern does not match
    if (!RegexPatterns.EMAIL_REGEX.test(email)) {
      setFormStatus({
        status: 'error',
        helperText: 'Please enter a valid email',
      });
      return;
    }
    const url = `${apiUrls.railsCheckoutV1}/prospects`;
    try {
      await axios.post(
        url,
        {
          tenant_id: user.tenantId,
          email: email,
          source: 'ecommerce-platform-footer',
          marketing_optin: 'EXPLICITLY_OPTEDIN',
        },
        { withCredentials: true },
      );
      setFormStatus({
        status: 'success',
        helperText: 'Subscribed!',
      });
    } catch (err: any) {
      const responseJson = await err?.response?.json();
      const emailValidationErrors = responseJson?.error?.validation?.email;
      const emailTaken =
        emailValidationErrors &&
        emailValidationErrors.length &&
        emailValidationErrors[0] === 'has already been taken';
      if (emailTaken) {
        setFormStatus({
          status: 'error',
          helperText: 'You are already signed up!',
        });
      } else {
        setFormStatus({
          status: 'error',
          helperText: GENERIC_ERROR_MESSAGE,
        });
        logger.error({ noticable: err, msg: 'failed to subscribe email' });
      }
    }
  };

  const theme = useTheme();

  return (
    <Grid
      container
      direction="column"
      spacing={1}
      css={isSmallScreen && smallScreenStyles}
    >
      <Grid>
        <Typography variant="h6" color={theme.palette.common.white}>
          Feed your inbox
        </Typography>
        <Typography variant="subtitle2" color={theme.palette.common.white}>
          Sign up for updates, news, and more.
        </Typography>
      </Grid>
      <Grid>
        <TextField
          color={formStatus.status === 'success' ? 'success' : 'primary'}
          className="text-field"
          variant="outlined"
          error={formStatus.status === 'error'}
          helperText={formStatus.helperText}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
          }}
          css={{
            input: {
              color: theme.palette.common.white,
            },
            root: {
              borderColor: theme.palette.common.white,
            },
            '& .MuiOutlinedInput-root, .MuiOutlinedInput-root:hover, .MuiOutlinedInput-root:focus, .MuiOutlinedInput-root:focus-within':
              {
                '& > fieldset': {
                  borderColor: theme.palette.common.white,
                },
              },
          }}
          FormHelperTextProps={{
            style:
              formStatus.status === 'success'
                ? {
                    color: theme.palette.success.light,
                  }
                : undefined,
          }}
        />
      </Grid>
      <Grid>
        <Button onClick={handleSubmit} variant="contained">
          sign up
        </Button>
      </Grid>
    </Grid>
  );
};

export default EmailSignup;
